import { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ArrowLeft, 
  BookOpen, 
  Users, 
  Hammer, 
  Palette, 
  Library, 
  Truck, 
  Clock, 
  MapPin, 
  CheckCircle2,
  Camera,
  DollarSign,
  Mail,
  Globe,
  Heart,
  Building2,
  Calendar
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ab04b277-779a-46f6-a15c-41b90e7d7254/artifacts/ccdpbqku_TBP-New%20Logo%20Design%20conv%201.png";
const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CorporateToolkit() {
  const { corporateId } = useParams();
  const [searchParams] = useSearchParams();
  const partnerId = corporateId || searchParams.get('id');
  
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(!!partnerId);

  useEffect(() => {
    if (partnerId) {
      fetchPartnerDetails();
    }
  }, [partnerId]);

  const fetchPartnerDetails = async () => {
    try {
      const response = await fetch(`${API}/corporate/toolkit/${partnerId}`);
      if (response.ok) {
        const data = await response.json();
        setPartner(data);
      }
    } catch (error) {
      console.error('Failed to fetch partner details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format date nicely
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const dt = new Date(dateStr);
      return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="corporate-toolkit-page">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <Link to="/">
                <img src={LOGO_URL} alt="The Bookshelf Project" className="h-12 w-auto" />
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/corporate">
                <Button variant="ghost" className="text-slate-600 hover:text-[#1a3a5c]">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Corporate
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-[#1a3a5c] to-[#2a5a8c]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Corporate Volunteer Toolkit
          </h1>
          <p className="text-xl text-white/80">
            {partner ? `Prepared for ${partner.company_name}` : 'Everything your team needs to prepare for a successful service day'}
          </p>
        </div>
      </section>

      {/* Personalized Partner Details (if available) */}
      {partner && (
        <section className="py-8 bg-[#dbeafe]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="border-2 border-[#1a3a5c] shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="w-8 h-8 text-[#1a3a5c]" />
                  <h2 className="text-2xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Your Service Day Details
                  </h2>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-slate-500">Company</p>
                      <p className="text-lg font-semibold text-[#1a3a5c]">{partner.company_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Contact</p>
                      <p className="text-lg font-semibold text-[#1a3a5c]">{partner.contact_name}</p>
                    </div>
                    {partner.estimated_volunteers > 0 && (
                      <div>
                        <p className="text-sm text-slate-500">Team Size</p>
                        <p className="text-lg font-semibold text-[#1a3a5c]">{partner.estimated_volunteers} volunteers</p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    {partner.preferred_date_range && (
                      <div>
                        <p className="text-sm text-slate-500">Preferred Date</p>
                        <p className="text-lg font-semibold text-[#1a3a5c] flex items-center gap-2">
                          <Calendar className="w-5 h-5" />
                          {formatDate(partner.preferred_date_range)}
                        </p>
                      </div>
                    )}
                    {partner.preferred_time && (
                      <div>
                        <p className="text-sm text-slate-500">Time Window</p>
                        <p className="text-lg font-semibold text-[#1a3a5c] flex items-center gap-2">
                          <Clock className="w-5 h-5" />
                          {partner.preferred_time}
                        </p>
                      </div>
                    )}
                    {partner.preferred_activities && partner.preferred_activities.length > 0 && (
                      <div>
                        <p className="text-sm text-slate-500">Selected Activities</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {partner.preferred_activities.map((activity, i) => (
                            <span key={i} className="px-3 py-1 bg-[#5cc8e8]/20 text-[#1a3a5c] rounded-full text-sm font-medium">
                              {activity}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Welcome Message */}
      <section className="py-12 bg-[#5cc8e8]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-4">
                <Heart className="w-8 h-8 text-[#5cc8e8]" />
                <h2 className="text-2xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {partner ? `Welcome, ${partner.company_name}!` : 'Welcome Message'}
                </h2>
              </div>
              <div className="prose prose-slate max-w-none">
                <p className="text-lg text-slate-700 leading-relaxed mb-4">
                  <strong>Welcome to The Bookshelf Project Corporate Volunteer Program!</strong>
                </p>
                <p className="text-slate-600 leading-relaxed mb-4">
                  {partner 
                    ? `Thank you for partnering with us! We're excited to work with ${partner.company_name} to help build in-home libraries for children. Your team's service helps ensure that children have access to books, a dedicated reading space, and the opportunity to develop a lifelong love of reading.`
                    : 'Thank you for partnering with us to help build in-home libraries for children. Your team\'s service helps ensure that children have access to books, a dedicated reading space, and the opportunity to develop a lifelong love of reading.'
                  }
                </p>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Through your volunteer service day, your team will play a direct role in helping children and families build strong literacy foundations.
                </p>
                <p className="text-slate-700 font-medium italic">
                  Together, we are helping create homes where books live and learning begins.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
        
        {/* Section 1: About */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#1a3a5c] rounded-full flex items-center justify-center text-white font-bold">1</div>
            <h2 className="text-2xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              About The Bookshelf Project
            </h2>
          </div>
          <Card className="border-0 shadow-md">
            <CardContent className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-[#1a3a5c] mb-2">Mission</h3>
                <p className="text-slate-600 leading-relaxed">
                  The Bookshelf Project is a Washington, DC–based nonprofit dedicated to cultivating a love for books, reading, and lifelong learning for children from birth through age eight.
                </p>
                <p className="text-slate-600 leading-relaxed mt-2">
                  We work to close literacy gaps by helping families create in-home libraries filled with culturally relevant books and by providing family literacy support.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#1a3a5c] mb-2">Why This Work Matters</h3>
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    Children with books in the home are more likely to succeed in school
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    Many children in underserved communities have few or no books at home
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    Early literacy exposure shapes brain development and academic outcomes
                  </li>
                </ul>
                <p className="text-slate-700 font-medium mt-4">
                  Your volunteer service helps change this reality for families.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Section 2: Volunteer Opportunities */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#1a3a5c] rounded-full flex items-center justify-center text-white font-bold">2</div>
            <h2 className="text-2xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Corporate Volunteer Opportunities
            </h2>
          </div>
          <p className="text-slate-600 mb-6">Companies can support The Bookshelf Project through several service activities.</p>
          
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-0 shadow-md border-l-4 border-l-amber-500">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <Hammer className="w-6 h-6 text-amber-500" />
                  <h3 className="font-semibold text-[#1a3a5c]">Bookshelf Assembly Day</h3>
                </div>
                <p className="text-sm text-slate-600 mb-3">Volunteers assemble bookshelves that will be delivered to families.</p>
                <ul className="text-sm text-slate-500 space-y-1">
                  <li>• Assembling bookshelves</li>
                  <li>• Quality inspection and safety checks</li>
                  <li>• Packaging bookshelves for delivery</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md border-l-4 border-l-pink-500">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <Palette className="w-6 h-6 text-pink-500" />
                  <h3 className="font-semibold text-[#1a3a5c]">Bookshelf Artisan Day</h3>
                </div>
                <p className="text-sm text-slate-600 mb-3">Volunteers decorate bookshelves to make them vibrant and child-friendly.</p>
                <ul className="text-sm text-slate-500 space-y-1">
                  <li>• Painting and decorating shelves</li>
                  <li>• Adding literacy-themed designs</li>
                  <li>• Personalizing with positive messages</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md border-l-4 border-l-emerald-500">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <Library className="w-6 h-6 text-emerald-500" />
                  <h3 className="font-semibold text-[#1a3a5c]">Book Sorting & Packing</h3>
                </div>
                <p className="text-sm text-slate-600 mb-3">Volunteers organize and bundle books for families.</p>
                <ul className="text-sm text-slate-500 space-y-1">
                  <li>• Sorting books by age group</li>
                  <li>• Creating family book bundles</li>
                  <li>• Packaging books for distribution</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md border-l-4 border-l-blue-500">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <Truck className="w-6 h-6 text-blue-500" />
                  <h3 className="font-semibold text-[#1a3a5c]">Bookshelf Delivery Day</h3>
                </div>
                <p className="text-sm text-slate-600 mb-3">Volunteers help deliver bookshelves and books to families.</p>
                <ul className="text-sm text-slate-500 space-y-1">
                  <li>• Loading bookshelves</li>
                  <li>• Delivering to families</li>
                  <li>• Helping families set up their library</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Section 3: What to Expect */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#1a3a5c] rounded-full flex items-center justify-center text-white font-bold">3</div>
            <h2 className="text-2xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              What to Expect on Your Volunteer Day
            </h2>
          </div>
          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <p className="text-slate-600 mb-4">Typical corporate service days last <strong>2–4 hours</strong>.</p>
              <p className="text-slate-600 mb-4">A standard service day includes:</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  'Welcome and program overview',
                  'Volunteer orientation and safety briefing',
                  'Volunteer activity (assembly, painting, etc.)',
                  'Impact reflection and group photo'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 bg-slate-50 p-3 rounded-lg">
                    <div className="w-6 h-6 bg-[#5cc8e8] rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {i + 1}
                    </div>
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-slate-700 mt-4 font-medium">
                Your team will see firsthand how their work helps children build reading habits at home.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Section 4: Logistics */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#1a3a5c] rounded-full flex items-center justify-center text-white font-bold">4</div>
            <h2 className="text-2xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Volunteer Day Logistics
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-0 shadow-md">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-5 h-5 text-[#5cc8e8]" />
                  <h3 className="font-semibold text-[#1a3a5c]">Arrival Time</h3>
                </div>
                <p className="text-sm text-slate-600">
                  Please arrive <strong>10–15 minutes</strong> before the start time for check-in.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-5 h-5 text-[#5cc8e8]" />
                  <h3 className="font-semibold text-[#1a3a5c]">What to Wear</h3>
                </div>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Comfortable clothing</li>
                  <li>• Closed-toe shoes</li>
                  <li>• Clothing appropriate for painting or light assembly</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md md:col-span-2">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  <h3 className="font-semibold text-[#1a3a5c]">What We Provide</h3>
                </div>
                <div className="grid sm:grid-cols-3 gap-2 text-sm text-slate-600">
                  <span>• Tools and materials</span>
                  <span>• Paint and supplies</span>
                  <span>• Work instructions</span>
                  <span>• Safety equipment</span>
                  <span>• Volunteer orientation</span>
                  <span>• Snacks and refreshments</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Section 5: Impact */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#1a3a5c] rounded-full flex items-center justify-center text-white font-bold">5</div>
            <h2 className="text-2xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Volunteer Impact
            </h2>
          </div>
          <Card className="border-0 shadow-lg bg-gradient-to-br from-[#1a3a5c] to-[#2a5a8c] text-white">
            <CardContent className="p-6">
              <p className="text-white/80 mb-6">Your team's service can create meaningful impact in just a few hours.</p>
              <h3 className="font-semibold mb-4 text-[#5cc8e8]">Example Service Day Impact</h3>
              <div className="grid grid-cols-5 gap-2 text-center">
                <div className="bg-white/10 rounded-lg p-3">
                  <p className="text-2xl font-bold">25</p>
                  <p className="text-xs text-white/70">Volunteers</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3">
                  <p className="text-2xl font-bold">4</p>
                  <p className="text-xs text-white/70">Hours</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3">
                  <p className="text-2xl font-bold">40</p>
                  <p className="text-xs text-white/70">Bookshelves</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3">
                  <p className="text-2xl font-bold">40</p>
                  <p className="text-xs text-white/70">Families</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3">
                  <p className="text-2xl font-bold">200+</p>
                  <p className="text-xs text-white/70">Books</p>
                </div>
              </div>
              <p className="text-white/90 mt-6 text-center font-medium">
                Each bookshelf becomes the foundation of a child's first in-home library.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Section 6: Sponsorship */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#1a3a5c] rounded-full flex items-center justify-center text-white font-bold">6</div>
            <h2 className="text-2xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Corporate Sponsorship Opportunities
            </h2>
          </div>
          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <p className="text-slate-600 mb-4">Companies can expand their impact by sponsoring the bookshelves their team builds.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 text-[#1a3a5c]">Sponsorship Level</th>
                      <th className="text-left py-2 text-[#1a3a5c]">Impact</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-3 font-medium text-emerald-600">$1,000</td>
                      <td className="py-3 text-slate-600">25 bookshelves</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 font-medium text-emerald-600">$2,500</td>
                      <td className="py-3 text-slate-600">60 bookshelves</td>
                    </tr>
                    <tr>
                      <td className="py-3 font-medium text-emerald-600">$5,000</td>
                      <td className="py-3 text-slate-600">120 bookshelves</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-4 bg-slate-50 rounded-lg p-4">
                <p className="text-sm font-medium text-[#1a3a5c] mb-2">Sponsors may receive:</p>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Recognition on our website</li>
                  <li>• Social media acknowledgment</li>
                  <li>• Inclusion in impact reports</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Section 7: Social Media */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#1a3a5c] rounded-full flex items-center justify-center text-white font-bold">7</div>
            <h2 className="text-2xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Social Media & Photo Sharing
            </h2>
          </div>
          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <p className="text-slate-600 mb-4">We love sharing volunteer moments! If your company shares photos, please tag:</p>
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                  <Camera className="w-4 h-4 inline mr-2" />
                  @TheBookshelfProject
                </div>
                <div className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                  The Bookshelf Project
                </div>
              </div>
              <p className="text-slate-600">Suggested hashtag: <span className="font-semibold text-[#5cc8e8]">#BookshelfProject</span></p>
            </CardContent>
          </Card>
        </section>

        {/* Section 8: Checklist */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#1a3a5c] rounded-full flex items-center justify-center text-white font-bold">8</div>
            <h2 className="text-2xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Volunteer Group Checklist
            </h2>
          </div>
          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <p className="text-slate-600 mb-4">Before your service day, please confirm:</p>
              <div className="space-y-3">
                {[
                  'Number of volunteers attending',
                  'Volunteer waivers completed',
                  'Arrival time confirmed',
                  'Parking or transportation needs',
                  'Any sponsorship or donation details'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-6 h-6 border-2 border-emerald-500 rounded flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Section 9: After */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#1a3a5c] rounded-full flex items-center justify-center text-white font-bold">9</div>
            <h2 className="text-2xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              After Your Volunteer Day
            </h2>
          </div>
          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <p className="text-slate-600 mb-4">After your service day, your company will receive:</p>
              <ul className="text-slate-600 space-y-2 mb-6">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  A volunteer impact summary
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  Photos from the event
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  Documentation of volunteer hours (if needed)
                </li>
              </ul>
              <p className="text-slate-600 mb-3">We also welcome ongoing partnerships through:</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-[#5cc8e8]/10 text-[#1a3a5c] rounded-full text-sm">Annual service days</span>
                <span className="px-3 py-1 bg-[#5cc8e8]/10 text-[#1a3a5c] rounded-full text-sm">Literacy sponsorships</span>
                <span className="px-3 py-1 bg-[#5cc8e8]/10 text-[#1a3a5c] rounded-full text-sm">Book drives</span>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Section 10: Contact */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#1a3a5c] rounded-full flex items-center justify-center text-white font-bold">10</div>
            <h2 className="text-2xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Contact Information
            </h2>
          </div>
          <Card className="border-0 shadow-lg bg-gradient-to-br from-slate-50 to-slate-100">
            <CardContent className="p-6 text-center">
              <h3 className="text-xl font-bold text-[#1a3a5c] mb-2">The Bookshelf Project</h3>
              <p className="text-slate-600 mb-4">Volunteer Partnerships Team</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="mailto:partnerships@thebookshelfproject.org" className="flex items-center justify-center gap-2 text-[#5cc8e8] hover:underline">
                  <Mail className="w-4 h-4" />
                  partnerships@thebookshelfproject.org
                </a>
                <a href="https://thebookshelfproject.org" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 text-[#5cc8e8] hover:underline">
                  <Globe className="w-4 h-4" />
                  thebookshelfproject.org
                </a>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>

      {/* Footer */}
      <footer className="bg-[#1a3a5c] border-t border-white/10 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Link to="/" className="text-white font-bold text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              The Bookshelf Project
            </Link>
            <p className="text-white/60 text-sm">
              © {new Date().getFullYear()} The Bookshelf Project. Building in-home libraries for children.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
